import {
  Container,
  DesignTokenSizeEnum,
  Text,
  TokenTextAppearance,
  TokenTextColor,
  TextTag,
} from '@volkswagen-onehub/components-core';
import type { HTMLAttributes, ReactNode } from 'react';
import DropdownOverlay from '../DropdownOverlay/DropdownOverlay';
import PageContainer from '../PageContainer/PageContainer';
import PriceBreakdownTable, {
  PriceBreakdownTableProps,
} from '../PriceBreakdownTable/PriceBreakdownTable';
import FixedTopContainer from '../FixedTopContainer/FixedTopContainer';
import {
  DropdownTextWrapper,
  P11dPriceWrapper,
  TotalPriceWrapper,
} from './PriceBreakdownDropdown.styles';
import { formatCurrency } from '@/modules/utils/currency';
import Superscript from '../Superscript/Superscript';
import { DisclaimerText } from '@/styles';
import useResizeResponsive from '@/hooks/useResizeResponsive';
import { isMobile } from '@/modules/utils/breakpoints';

export interface PriceBreakdownDropdownProps
  extends HTMLAttributes<HTMLDivElement> {
  title: string;
  subtitle?: string;
  totalPriceLabel: string;
  totalPrice?: number | null | undefined;
  priceBreakdown: PriceBreakdownTableProps;
  disclaimerText: ReactNode;
  reveal: boolean;
  p11dLabel: string;
  p11dPrice?: number | null | undefined;
}

function PriceBreakdownDropdown(props: PriceBreakdownDropdownProps) {
  const {
    title,
    subtitle,
    totalPrice,
    totalPriceLabel,
    p11dLabel,
    p11dPrice,
    priceBreakdown,
    disclaimerText,
    reveal,
  } = props;

  const isMobileWidth = useResizeResponsive(isMobile);

  const DropdownText = () => (
    <DropdownTextWrapper>
      <Container wrap="always" gutter="dynamic0020">
        <Text
          appearance={TokenTextAppearance.headline200}
          color={TokenTextColor.primary}
          tag={TextTag.p}
          bold
        >
          {title}
        </Text>
        <Text
          appearance={TokenTextAppearance.copy100}
          color={TokenTextColor.primary}
          tag={TextTag.p}
        >
          {subtitle}
        </Text>
      </Container>

      <TotalPriceWrapper>
        <P11dPriceWrapper>
          <Text
            appearance={TokenTextAppearance.headline200}
            color={TokenTextColor.primary}
            tag={TextTag.p}
          >
            {totalPriceLabel}
            <Superscript />
          </Text>
          <Text
            appearance={TokenTextAppearance.headline200}
            color={TokenTextColor.primary}
            tag={TextTag.p}
            bold
          >
            {formatCurrency(totalPrice)}
          </Text>
        </P11dPriceWrapper>
        {p11dPrice != null && (
          <P11dPriceWrapper>
            <Text
              appearance={
                isMobileWidth
                  ? TokenTextAppearance.headline100
                  : TokenTextAppearance.headline200
              }
              color={TokenTextColor.primary}
              tag={TextTag.p}
            >
              {p11dLabel}
            </Text>
            <Text
              appearance={
                isMobileWidth
                  ? TokenTextAppearance.headline100
                  : TokenTextAppearance.headline200
              }
              color={TokenTextColor.primary}
              tag={TextTag.p}
              bold
            >
              {formatCurrency(p11dPrice)}
            </Text>
          </P11dPriceWrapper>
        )}
      </TotalPriceWrapper>
    </DropdownTextWrapper>
  );

  return (
    <FixedTopContainer reveal={reveal}>
      <DropdownOverlay dropdown={<DropdownText />}>
        <PageContainer>
          <PriceBreakdownTable
            prices={priceBreakdown.prices}
            alignDesktop="end"
          />
          <DisclaimerText
            appearance={TokenTextAppearance.copy100}
            b960={{ marginTop: DesignTokenSizeEnum.static400 }}
          >
            {disclaimerText}
          </DisclaimerText>
        </PageContainer>
      </DropdownOverlay>
    </FixedTopContainer>
  );
}

export default PriceBreakdownDropdown;
